import { useEffect, useState, useCallback } from 'react';
import { getStats, getWorkouts, getReadiness, WorkoutStats, Workout, User, ReadinessData } from '../api/client';
import { WorkoutCard } from '../components/WorkoutCard';
import { StatsSkeleton, WorkoutCardSkeleton } from '../components/Skeleton';
import { ErrorState, EmptyState } from '../components/States';
import { haptic } from '../lib/telegram';
import { formatLongDate } from '../lib/date';
import { ReadinessCard } from '../components/ReadinessCard';
import './Dashboard.css';

interface DashboardProps {
  user: User;
  onNavigate: (page: string, data?: any) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onNavigate }) => {
  const [stats, setStats] = useState<WorkoutStats | null>(null);
  const [recent, setRecent] = useState<Workout[]>([]);
  const [readiness, setReadiness] = useState<ReadinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, w, r] = await Promise.all([
        getStats(user.id),
        getWorkouts(user.id, 3),
        getReadiness(user.id)
      ]);
      setStats(s);
      setRecent(w);
      setReadiness(r);
    } catch (err: any) {
      setError(err?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (user.id) load();
  }, [user.id, load]);

  const greeting = user.first_name ? `, ${user.first_name}` : '';

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <p className="dashboard-eyebrow">RUN LAB</p>
        <h1>Твои пробежки</h1>
        <p className="dashboard-greeting">Привет{greeting}! Собираем прогресс из отчётов Huawei Health.</p>
      </header>

      {readiness && <ReadinessCard userId={user.id} readiness={readiness} onChange={setReadiness} />}

      {loading ? (
        <StatsSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats?.thisWeek ?? 0}</div>
            <div className="stat-label">За неделю</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.thisMonth ?? 0}</div>
            <div className="stat-label">За месяц</div>
          </div>
          <div className="stat-card stat-card-accent">
            <div className="stat-value">{stats?.total ?? 0}</div>
            <div className="stat-label">Всего</div>
          </div>
        </div>
      )}

      <div className="quick-actions">
        <button
          className="action-button upload-button"
          onClick={() => { haptic.light(); onNavigate('upload'); }}
        >
          <span className="action-icon">＋</span>
          <span className="action-copy">
            <strong>Добавить тренировку</strong>
            <small>Импортировать длинный JPG-отчёт</small>
          </span>
          <span className="action-arrow">→</span>
        </button>
        <button
          className="action-button primary"
          onClick={() => { haptic.light(); onNavigate('calendar'); }}
        >
          Календарь
        </button>
        <button
          className="action-button secondary"
          onClick={() => { haptic.light(); onNavigate('ai'); }}
        >
          AI-разбор
        </button>
      </div>

      <section className="recent-workouts">
        <div className="section-header">
          <h2>Последние тренировки</h2>
          {recent.length > 0 && (
            <button
              className="view-all-button"
              onClick={() => { haptic.light(); onNavigate('workouts'); }}
            >
              Все →
            </button>
          )}
        </div>

        {loading ? (
          <div className="workouts-list">
            <WorkoutCardSkeleton />
            <WorkoutCardSkeleton />
          </div>
        ) : error ? null : recent.length === 0 ? (
          <EmptyState
            icon="—"
            title="Начните с первой пробежки"
            hint="Загрузите длинный скриншот Huawei Health — приложение сохранит темп, пульс и технику бега."
            actionLabel="Добавить отчёт"
            onAction={() => { haptic.light(); onNavigate('upload'); }}
          />
        ) : (
          <div className="workouts-list">
            {recent.map(w => (
              <WorkoutCard
                key={w.id}
                date={w.date}
                exercises={w.exercises}
                aiAnalysis={w.ai_analysis}
                runningStats={w.running_stats}
                score={w.score}
                onClick={() => { haptic.light(); onNavigate('workout-detail', { workoutId: w.id }); }}
              />
            ))}
          </div>
        )}
      </section>

      {stats?.lastWorkoutDate && (
        <p className="dashboard-footer-hint">
          Последняя тренировка: {formatLongDate(stats.lastWorkoutDate)}
        </p>
      )}
    </div>
  );
};
